(() => {
  const GLOBAL_KEY = '__kpCartMinimumGuard';
  const LINE_SELECTOR = '[data-kp-cart-minimum-line]';
  const CHECKOUT_SELECTOR = '[data-kp-cart-minimum-checkout], button[name="checkout"], input[name="checkout"]';
  const ACCELERATED_SELECTOR = '[data-kp-cart-accelerated-checkout]';
  const SUMMARY_SELECTOR = '[data-kp-cart-minimum-summary]';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].refresh();
    return;
  }

  let blocked = false;
  let frame = 0;

  const parseQuantity = (value) => {
    const parsed = Number.parseInt(String(value ?? ''), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const setDescription = (input, messageId, shouldDescribe) => {
    if (!(input instanceof HTMLInputElement) || !messageId) return;
    const ids = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    if (shouldDescribe) ids.add(messageId);
    else ids.delete(messageId);

    if (ids.size) input.setAttribute('aria-describedby', [...ids].join(' '));
    else input.removeAttribute('aria-describedby');
  };

  const updateLine = (line, apparelTotals) => {
    const minimum = parseQuantity(line.dataset.kpMinimum);
    const input = line.querySelector('input[name="updates[]"], input[data-cart-line]');
    const quantity = parseQuantity(input?.value ?? line.dataset.kpServerQuantity);
    const message = line.querySelector('[data-kp-cart-minimum-message]');
    const apparelModel = line.dataset.kpApparelLine === 'true' ? line.dataset.kpApparelModel : '';
    const apparelTotal = apparelModel ? (apparelTotals.get(apparelModel) || 0) : 0;
    const minimumInvalid = minimum > 1 && quantity < minimum;
    const apparelInvalid = Boolean(apparelModel) && apparelTotal > 100;
    const invalid = minimumInvalid || apparelInvalid;

    line.classList.toggle('kp-cart-minimum-invalid', invalid);

    if (input instanceof HTMLInputElement && apparelModel) {
      if (input.dataset.kpApparelOriginalMax === undefined) input.dataset.kpApparelOriginalMax = input.getAttribute('max') || '';
      const originalMax = parseQuantity(input.dataset.kpApparelOriginalMax);
      const otherQuantity = Math.max(0, apparelTotal - quantity);
      const modelMax = Math.max(0, 100 - otherQuantity);
      input.max = String(originalMax > 0 ? Math.min(originalMax, modelMax) : modelMax);
    }

    if (message instanceof HTMLElement) {
      message.hidden = !invalid;
      if (apparelInvalid) {
        message.textContent = 'Maximum 100 garments per apparel model. Reduce this model to 100 or fewer before checkout.';
      } else if (minimumInvalid) {
        message.textContent = 'Minimum ' + minimum + ' per kippah line. Increase this line to at least ' + minimum + ' or remove it before checkout.';
      } else if (message.textContent.trim()) {
        message.textContent = '';
      }
      setDescription(input, message.id, invalid);
    }

    if (input instanceof HTMLInputElement) {
      if (invalid) {
        input.setAttribute('aria-invalid', 'true');
        input.dataset.kpMinimumAriaInvalid = 'true';
      } else if (input.dataset.kpMinimumAriaInvalid === 'true') {
        input.removeAttribute('aria-invalid');
        delete input.dataset.kpMinimumAriaInvalid;
      }
    }

    return { minimumInvalid, apparelInvalid };
  };

  const updateCheckoutControls = (shouldBlock, reasons) => {
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((control) => {
      if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) return;

      if (shouldBlock) {
        if (!control.dataset.kpMinimumOriginalDisabled) {
          const serverBlocked = control.dataset.kpCartMinimumServerBlocked === 'true';
          control.dataset.kpMinimumOriginalDisabled = control.disabled && !serverBlocked ? 'true' : 'false';
        }
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      } else if (control.dataset.kpMinimumOriginalDisabled) {
        const keepDisabled = control.dataset.kpMinimumOriginalDisabled === 'true' || control.dataset.kpCartEmpty === 'true';
        control.disabled = keepDisabled;
        control.removeAttribute('aria-disabled');
        delete control.dataset.kpMinimumOriginalDisabled;
      }
    });

    document.querySelectorAll(ACCELERATED_SELECTOR).forEach((container) => {
      if (!(container instanceof HTMLElement)) return;
      container.inert = shouldBlock;
      if (shouldBlock) container.setAttribute('aria-disabled', 'true');
      else container.removeAttribute('aria-disabled');
    });

    document.querySelectorAll(SUMMARY_SELECTOR).forEach((summary) => {
      if (!(summary instanceof HTMLElement)) return;
      summary.hidden = !shouldBlock;
      if (reasons.apparelInvalid && reasons.minimumInvalid) {
        summary.textContent = 'Some kippah lines are below their minimum, and apparel is limited to 100 garments per model. Update the marked lines before checkout.';
      } else if (reasons.apparelInvalid) {
        summary.textContent = 'Apparel is limited to 100 garments per model. Reduce each marked model to 100 or fewer before checkout.';
      } else if (reasons.minimumInvalid) {
        summary.textContent = 'Some kippah lines are below the 10-per-line minimum. Update each marked line or remove it before checkout.';
      } else if (summary.textContent.trim()) {
        summary.textContent = '';
      }
    });
  };

  const updateMixedProductionNotes = () => {
    let hasApparel = false;
    let hasKippahs = false;
    document.querySelectorAll(LINE_SELECTOR).forEach((line) => {
      if (line.closest('[hidden]')) return;
      const input = line.querySelector('input[name="updates[]"], input[data-cart-line]');
      const quantity = parseQuantity(input?.value ?? line.dataset.kpServerQuantity);
      if (quantity <= 0) return;
      if (line.dataset.kpApparelLine === 'true') hasApparel = true;
      else if (parseQuantity(line.dataset.kpMinimum) === 10) hasKippahs = true;
    });
    document.querySelectorAll('[data-kp-mixed-production-note]').forEach((note) => {
      const hide = !(hasApparel && hasKippahs);
      if (note.hidden !== hide) note.hidden = hide;
    });
  };

  const refresh = () => {
    frame = 0;
    const lines = [...document.querySelectorAll(LINE_SELECTOR)];
    const records = new Map();
    lines.forEach((line, index) => {
      if (line.dataset.kpApparelLine !== 'true' || !line.dataset.kpApparelModel) return;
      const input = line.querySelector('input[name="updates[]"], input[data-cart-line]');
      const record = {
        model: line.dataset.kpApparelModel,
        quantity: parseQuantity(input?.value ?? line.dataset.kpServerQuantity),
        visible: line.getClientRects().length > 0 && !line.closest('[hidden]'),
      };
      const key = line.dataset.key || 'cart-line-' + index;
      if (!records.has(key) || record.visible) records.set(key, record);
    });
    const apparelTotals = new Map();
    records.forEach(({ model, quantity }) => apparelTotals.set(model, (apparelTotals.get(model) || 0) + Math.max(0, quantity)));

    let minimumInvalid = false;
    let apparelInvalid = false;
    lines.forEach((line) => {
      const result = updateLine(line, apparelTotals);
      minimumInvalid ||= result.minimumInvalid;
      apparelInvalid ||= result.apparelInvalid;
    });
    blocked = minimumInvalid || apparelInvalid;
    document.documentElement.classList.toggle('kp-cart-minimums-blocked', blocked);
    updateCheckoutControls(blocked, { minimumInvalid, apparelInvalid });
    updateMixedProductionNotes();
    return blocked;
  };

  const schedule = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(refresh);
  };

  const focusFirstInvalidLine = () => {
    const line = document.querySelector(LINE_SELECTOR + '.kp-cart-minimum-invalid');
    const target = line?.querySelector('input[name="updates[]"], input[data-cart-line], [data-kp-cart-minimum-message]');
    if (target instanceof HTMLElement) target.focus({ preventScroll: false });
  };

  document.addEventListener('input', (event) => {
    if (event.target instanceof Element && event.target.closest(LINE_SELECTOR)) schedule();
  }, true);

  document.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.closest(LINE_SELECTOR)) schedule();
  }, true);

  document.addEventListener('click', (event) => {
    if (!blocked || !(event.target instanceof Element)) return;
    const checkoutTarget = event.target.closest(CHECKOUT_SELECTOR + ', ' + ACCELERATED_SELECTOR + ' button, ' + ACCELERATED_SELECTOR + ' [role="button"]');
    if (!checkoutTarget) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    focusFirstInvalidLine();
  }, true);

  document.addEventListener('submit', (event) => {
    if (!blocked || !(event.target instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    const checkoutIntent = submitter instanceof Element && submitter.matches(CHECKOUT_SELECTOR);
    const checkoutAction = event.target.action && new URL(event.target.action, window.location.href).pathname.startsWith('/checkout');
    if (!checkoutIntent && !checkoutAction) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    focusFirstInvalidLine();
  }, true);

  ['shopify:section:load', 'cart:refresh', 'cart:update', 'cart:updated'].forEach((eventName) => {
    document.addEventListener(eventName, schedule);
  });
  window.addEventListener('pageshow', schedule);

  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window[GLOBAL_KEY] = {
    refresh,
    destroy() {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      delete window[GLOBAL_KEY];
    },
  };

  schedule();
})();
